import { useState, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DragAndDropFileUploader } from "@/components/drag-and-drop/DragAndDropFileUploader";
import { useUploadEstimationFiles } from "@/components/estimation-details/upload/hooks/useUploadEstimationFiles";
import { useAddDocumentationMutation } from "@/hooks/mutation/estimates/useAddDocumentationMutation";
import { UploadedFile } from "./UploadedFile";
import { UploadedFileProgress } from "./UploadedFileProgress";
import { EstimateType } from "@/enums/estimate-type";
import type { Estimation } from "@/interfaces/estimation.interface";
import { appSelector } from "@/redux/selectors/app.selector";
import { useSelector } from "@/redux/store";
import { FileText, Link } from "lucide-react";
import { useDebounceState } from "@/hooks/useDebounceState";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import estimateProvider from "@/providers/estimate.provider";
import {
  getFileUploadConfig,
  type DocusketchUploadState,
  DOCUSKETCH_UPLOAD_STATE_MESSAGES,
  DOCUSKETCH_PLAYER_URL_REGEX,
} from "./addDocumentationDialog.utils";
import { addDocumentationSchema, type AddDocumentationFormData } from "./addDocumentationDialog.schema";
import {
  clearScopeNoteDraft,
  getExistingEstimateScopeNoteDraftId,
  readScopeNoteDraft,
  writeScopeNoteDraft,
} from "./scopeNoteDraftStorage";

interface AddDocumentationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimateId: string;
  estimation: Estimation;
}

export const AddDocumentationDialog = ({ open, onOpenChange, estimateId, estimation }: AddDocumentationDialogProps) => {
  const user = useSelector(appSelector.user);
  const userId = user?.lassoUserId;
  const organizationId = user?.organizationId;
  const scopeNoteDraftParams = useMemo(
    () => ({
      userId,
      organizationId,
      draftId: getExistingEstimateScopeNoteDraftId(estimateId),
    }),
    [estimateId, organizationId, userId],
  );
  const addDocumentation = useAddDocumentationMutation();
  const { isFeatureEnabled } = useFeatureFlags();

  const {
    control,
    handleSubmit,
    watch,
    reset: resetForm,
    formState: { isSubmitting },
  } = useForm<AddDocumentationFormData>({
    resolver: zodResolver(addDocumentationSchema),
    defaultValues: { scopeNoteText: "", workAfterHours: false, docusketchLink: "" },
  });

  // DocuSketch state
  const docusketchLinkValue = watch("docusketchLink");
  const docusketchLinkDebounced = useDebounceState(docusketchLinkValue, 250);
  const [docusketchUploadState, setDocusketchUploadState] = useState<DocusketchUploadState>("EMPTY");

  const { reset, uploadedFiles, isUploading, uploadProgress, removeUploadedFile, uploadMultipleFiles } =
    useUploadEstimationFiles(estimateId);

  useEffect(() => {
    if (open) {
      resetForm({
        scopeNoteText: readScopeNoteDraft(scopeNoteDraftParams) ?? "",
        workAfterHours: false,
        docusketchLink: "",
      });
      setDocusketchUploadState("EMPTY");
      reset();
    }
  }, [open, reset, resetForm, scopeNoteDraftParams]);

  // DocuSketch link validation
  useEffect(() => {
    let stale = false;

    if (!docusketchLinkDebounced) {
      setDocusketchUploadState("EMPTY");
      return;
    }

    const docusketchId = docusketchLinkDebounced.match(DOCUSKETCH_PLAYER_URL_REGEX)?.[1];
    if (!docusketchId) {
      setDocusketchUploadState("INVALID");
      return;
    }

    setDocusketchUploadState("VALIDATING");

    estimateProvider
      .validateDocusketch({ docusketchId })
      .then(({ status }) => {
        if (!stale) setDocusketchUploadState(status === 200 ? "VALIDATION_PASSED" : "INVALID");
      })
      .catch(() => {
        if (!stale) setDocusketchUploadState("INVALID");
      });

    return () => { stale = true; };
  }, [docusketchLinkDebounced]);

  const estimateType = estimation?.estimateType;
  const uploadConfig = useMemo(() => getFileUploadConfig(estimateType), [estimateType]);

  const docusketchFeatureEnabled = isFeatureEnabled("SHOW_DOCUSKETCH_UPLOAD");
  const showDocusketchInput = docusketchFeatureEnabled && !estimation?.docusketchId;
  const showDocusketchAlreadyUploaded = docusketchFeatureEnabled && !!estimation?.docusketchId;

  const hasValidatedDocusketch = docusketchUploadState === "VALIDATION_PASSED";

  // Inline submit-disabled logic (replaces isSubmitDisabled utility)
  const scopeNoteText = watch("scopeNoteText");
  const hasScopeNotes = !!scopeNoteText.trim();
  const hasFiles = uploadedFiles.length > 0;
  const submitDisabled =
    isUploading ||
    docusketchUploadState === "VALIDATING" ||
    (estimateType === EstimateType.BIOHAZARD || estimateType === EstimateType.TIME_AND_MATERIAL
      ? !hasScopeNotes
      : !hasScopeNotes && !hasFiles && !hasValidatedDocusketch);

  const onSubmit = async (data: AddDocumentationFormData) => {
    const trimmed = data.scopeNoteText.trim() || undefined;

    await addDocumentation.mutateAsync({
      estimateId,
      estimation,
      scopeNoteText: trimmed,
      scopeNoteWorkAfterHours: data.workAfterHours,
      userId,
      docusketchLink: hasValidatedDocusketch ? data.docusketchLink : undefined,
    });

    clearScopeNoteDraft(scopeNoteDraftParams);
    onOpenChange(false);
  };

  const isPending = addDocumentation.isPending;
  const docusketchMessage = DOCUSKETCH_UPLOAD_STATE_MESSAGES[docusketchUploadState];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl !bg-white">
        <DialogHeader>
          <DialogTitle>Add Documentation</DialogTitle>
          <DialogDescription>Add scope notes{uploadConfig.showFileUpload ? ` or upload files` : ""} to this project.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="add-doc-scope-notes">Scope Notes</Label>
              <Controller
                control={control}
                name="scopeNoteText"
                render={({ field }) => (
                  <Textarea
                    id="add-doc-scope-notes"
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      writeScopeNoteDraft(scopeNoteDraftParams, event.target.value);
                    }}
                    placeholder="Enter scope notes..."
                    rows={6}
                    className="resize-vertical"
                  />
                )}
              />
            </div>

            {estimateType === EstimateType.MITIGATION && (
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="workAfterHours"
                  render={({ field }) => (
                    <Checkbox
                      id="work-after-hours"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                  )}
                />
                <Label htmlFor="work-after-hours" className="cursor-pointer text-sm font-normal">
                  Work Performed After Hours
                </Label>
              </div>
            )}

            {showDocusketchInput && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="add-doc-docusketch-link">3D Scan Link</Label>
                <div className="relative">
                  <Link className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                  <Controller
                    control={control}
                    name="docusketchLink"
                    render={({ field }) => (
                      <Input
                        id="add-doc-docusketch-link"
                        type="url"
                        {...field}
                        placeholder="example.com"
                        className="pl-9"
                      />
                    )}
                  />
                </div>
                {docusketchMessage.message && (
                  <p className={`text-sm ${docusketchMessage.color}`}>{docusketchMessage.message}</p>
                )}
              </div>
            )}

            {showDocusketchAlreadyUploaded && (
              <div className="flex flex-col gap-2">
                <Label>3D Scan Link</Label>
                <p className="text-sm text-green-600">3D scan link already uploaded</p>
              </div>
            )}

            {uploadConfig.showFileUpload && (
              <div className="flex flex-col gap-2">
                <Label>{uploadConfig.title}</Label>
                <DragAndDropFileUploader
                  title={uploadConfig.title}
                  className="w-full"
                  icon={<FileText size={32} className="text-gray-600" />}
                  onChange={(files) => {
                    uploadMultipleFiles(
                      files,
                      uploadConfig.fileType,
                      uploadConfig.postUploadCommand,
                      estimation?.tpa ?? "",
                      estimation?.insuranceCarrier ?? "",
                    );
                  }}
                />
                <div className="space-y-2">
                  {uploadedFiles.map((file) => (
                    <UploadedFile key={file?.uploadId} file={file} onRemove={removeUploadedFile} />
                  ))}
                  {Object.values(uploadProgress).map((item, index) => (
                    <UploadedFileProgress key={index} name={item.fileName} progress={item.progress} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="accent"
              disabled={submitDisabled || isPending || isSubmitting}
              className={isUploading || isPending || isSubmitting ? "animate-pulse" : ""}
            >
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
